import { gql } from "lib/apolloClient";
import {
  assignmentFragment,
  studentDetailsFragment,
} from "ClassRoom/modules/ClassRoomFragments";

export const assessmentFragment = gql`
  fragment assessmentItem on Assessment {
    id
    type
    title {
      id
      value
    }
    academicTerm {
      id
      label
      lockingStatus
    }
    assessmentType {
      id
      value
    }
    image {
      id
      value
    }
    category {
      id
      label
    }
  }
`;

export const attachmentFragment = gql`
  fragment classMaterialsAttachmentItem on Attachment {
    id
    type
    title
    name
    mimeType
    url
    signedUrl
    thumbUrl
    __typename
  }
`;

export const linkedResourcesFragment = gql`
  fragment linkedResources on OrganizationResource {
    linkedResources {
      course {
        title
        id
        students {
          edges {
            node {
              ...studentItem
            }
          }
        }
      }
      students {
        edges {
          node {
            id
          }
        }
      }
    }
  }
  ${studentDetailsFragment.basicStudentDetails}
`;

const classMaterialsResource = gql`
  fragment classMaterialsResourceItem on OrganizationResource {
    id
    label
    displaySequence
    depth
    resourceType
    state
    attachment {
      ...classMaterialsAttachmentItem
    }
    item {
      ... on Assignment {
        ...assignmentItem
        assignedStudents(studentIds: $studentIds)
          @skip(if: $skipAssignedStudents) {
          id
          status
          submission {
            id
            statusV2
          }
          isEvaluationApplicable
          isEvaluated
        }
      }
      ... on Assessment {
        ...assessmentItem
      }
      ... on OrganizationResource {
        id
        attachment {
          ...classMaterialsAttachmentItem
        }
      }
    }
    parent: parentResource {
      id
    }
    childrenHierarchyInfo(filters: $filters) {
      maxDepth
      totalCount
      totalCountWithFolder
    }
    students {
      totalCount
    }
  }
  ${assignmentFragment.assignmentFeedWithoutAssignedStudents}
  ${assessmentFragment}
  ${attachmentFragment}
`;

const classMaterialsResourceWithoutDepth = gql`
  fragment classMaterialsResourceItem on OrganizationResource {
    id
    label
    resourceType
    state
    attachment {
      ...classMaterialsAttachmentItem
    }
    item {
      ... on Assignment {
        ...assignmentItem
        assignedStudents(studentIds: $studentIds)
          @skip(if: $skipAssignedStudents) {
          id
          status
          submission {
            id
            statusV2
          }
          isEvaluationApplicable
          isEvaluated
        }
      }
      ... on Assessment {
        ...assessmentItem
      }
      ... on OrganizationResource {
        id
        attachment {
          ...classMaterialsAttachmentItem
        }
      }
    }
    parent: parentResource {
      id
    }
    childrenHierarchyInfo(filters: $filters) {
      maxDepth
      totalCount
      totalCountWithFolder
    }
    students {
      totalCount
    }
  }
  ${assignmentFragment.assignmentFeedWithoutAssignedStudents}
  ${assessmentFragment}
  ${attachmentFragment}
`;

export const classMaterialsFragment = {
  classMaterialsResource,
  classMaterialsWithChildren: gql`
    fragment classMaterialsWithChildrenItem on OrganizationResource {
      ...classMaterialsResourceItem
      childrenV2(orderBy: $orderBy, filters: $filters) {
        edges {
          node {
            id
            label
            resourceType
            displaySequence
          }
        }
      }
      childrenHierarchyInfo(filters: $filters) {
        maxDepth
        totalCount
        totalCountWithFolder
      }
    }
    ${classMaterialsResource}
  `,
};

export const classMaterialsFragmentWithoutDepth = {
  classMaterialsResource,
  classMaterialsWithChildren: gql`
    fragment classMaterialsWithChildrenItem on OrganizationResource {
      ...classMaterialsResourceItem
      childrenV2(orderBy: $orderBy, filters: $filters, first: 1000) {
        edges {
          node {
            id
            label
            resourceType
          }
        }
      }
      childrenHierarchyInfo(filters: $filters) {
        maxDepth
        totalCount
        totalCountWithFolder
      }
    }
    ${classMaterialsResourceWithoutDepth}
  `,
};

export const classMaterialOutlineFragment = {
  classMaterialsWithChildren: gql`
    fragment classMaterialsWithChildrenItem on OrganizationResource {
      childrenV2(orderBy: $orderBy, filters: $filters) {
        edges {
          node {
            id
          }
        }
      }
      parent: parentResource {
        id
      }
    }
  `,
};

export const singleCourseV2Fragment = {
  singleCourseV2: gql`
    fragment courseV2 on Course {
      id
      title
      isBookmark
      isArchived
      profileImageData {
        url
        icon
      }
      gradesWithAcademicYear {
        grade {
          id
          name
        }
        academicYear {
          id
          startDate
          endDate
        }
      }
      grade {
        id
        name
      }
      subjects {
        id
        name
      }
      grades(filters: $courseGradeFilters) {
        id
        name
      }
      genericTags(filters: { types: SUBJECT_LEVEL }) {
        id
        label
        type
      }
      primaryGrade(filters: $courseGradeFilters) {
        id
        name
      }
      subjectGroups {
        id
        name
        type
      }
      academicYears {
        id
      }
    }
  `,
};
